import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import BlogArticle from 'components/blog/BlogArticle';
import BlogLink from 'components/blog/BlogLink';
import MainLayout from 'components/layout/MainLayout';
import { NextSeo } from 'next-seo';
import { getCta } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/Top-8-Christmas-Stocking-Fillers-2022`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="Top 8 Stocking Fillers of 2022 | Win Epic Prizes | DAYMADE"
        description="Discover DAYMADE’s top 8 stocking fillers for 2022 to make your loved ones feel extra merry. Make your Christmas morning the best one yet with epic experiences, homemade goodies and sustainable beauty. Enter to win."
        openGraph={{
          title: 'Top 8 Stocking Fillers of 2022 | Win Epic Prizes | DAYMADE',
          description:
            'Discover DAYMADE’s top 8 stocking fillers for 2022 to make your loved ones feel extra merry. Make your Christmas morning the best one yet with epic experiences, homemade goodies and sustainable beauty. Enter to win.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/Top-8-Stocking-Fillers-2022',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Four Christmas Stockings on a fireplace',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'christmas stockings,stocking fillers,christmas presents',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Four Christmas Stockings on a fireplace"
        heroImageYPosition="0%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            DAYMADE’s Top 8 Stocking Fillers of 2022
          </h1>

          <p className="subheading">
            Have the ultimate Christmas morning with these unique festive
            treats.
          </p>

          <div className="separator" style={{ width: '80%', marginTop: 0 }} />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Violet Currey
          </p>

          <p>
            The most wonderful time of the year is finally here. The sweet
            festive smells of Christmas markets, feasts galore and cosy family
            get-togethers. With the holiday season officially in full swing,
            it's time to make sure your gifts bring some cheer on the big day.
            We all love Christmas morning in our PJs and opening those stockings
            to see what Santa has given us (you're never too old!). Always
            filled with the classic socks, tangerines (gotta get one of your
            five a day in!), chocolates, and more fun knick-knacks. As much fun
            as they are to kick off the big day, we all know we'll never use
            them. Millions of unwanted presents end up in landfills every year,
            plus all of the wrapping paper, which makes it one of the most
            wasteful holidays.
          </p>

          <p>
            This Christmas, let’s mix it up and fill up your stockings with
            sustainable goodies, experiences, and handmade creations to give our
            planet a break this year. By being more mindful and conscious
            towards our gift shopping, we'll have all the festive cheer minus
            the waste. Discover our top stocking fillers to reduce your waste,
            gift some unique presents, and let the planet have some joy too.
          </p>

          <h2>A lovingly presented batch of homemade, delicious treats</h2>

          <p>
            Christmas is all about one thing, eating to your heart’s content!
            The month of December is a time to get stuck into all your festive
            faves, cook up a storm, and tuck into the best sweet treats. A
            stocking isn’t complete without some cheeky treats, but how about
            making your own ones at home? Get baking your gingerbread men,
            Christmas cookies, and even some brownies with festive flavours. Get
            creative, and make your loved ones feel so good that they can't move
            this holiday season.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="Christmas gingerbread men and cookies"
            className="singleBlogImage"
          />

          <h2>A mini indoor plant</h2>

          <p>
            The gift of a new plant friend is a stocking filler must-have this
            festive season. A mini indoor plant that will definitely last longer
            than a bottle of wine or cheese. You'll give your pals a chance to
            create or add to their urban jungle, which ultimately cleans our air
            and makes us feel relaxed. Need to fill up a few stockings this
            year? Our{' '}
            <BlogLink internal="/prizes/details/patch-plants-50">
              5 Patch Plants
            </BlogLink>{' '}
            prize is the perfect way to share the joy of caring for plants this
            Christmas.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="A variety of indoor plants"
            className="singleBlogImage"
          />

          <h2>An invitation to a dinner party</h2>

          <p>
            Don’t let Christmas dinner be the only thing your pals are looking
            forward to. This one should definitely be organised after Christmas
            once your feast has gone down. Instead of buying more food for them
            (which might go to waste), why not invite them to a dinner party to
            show off your chef skills? Wine and dine your pals by cooking up a
            storm with some fancy wine, cheese, and desserts to really impress
            them. Nothing beats a dinner party, and this one will definitely
            jazz up your fillers this year to beat those January blues. Not sure
            where to start? We’ve got some of our fave cooking kits from{' '}
            <BlogLink internal="/prizes/details/wellington-home-dining">
              Wellingtons
            </BlogLink>{' '}
            to{' '}
            <BlogLink internal="/prizes/details/bottomless-brunch-sushi-kit">
              Bottomless Sushi
            </BlogLink>{' '}
            to really go for gold for the ultimate dinner party.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="A group of friends at a dinner party"
            className="singleBlogImage"
          />

          <h2>A homemade photo album of shared memories.</h2>

          <p>
            It’s time to dig up those old photos we all love (and sometimes
            regret!). A homemade photo album gives you a chance to get creative
            and make something personalised for your friends and family. A photo
            album is definitely a gift they will treasure, rather than getting
            stuck in the back of the cupboard and thrown away in a few years.
            Get cutting, sticking and digging through those embarrassing photos
            that we all treasure. We know we all have them. A great way to look
            back on memories whilst creating new ones on the big day.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5.jpg`}
            alt="A Young Girl Holding a Photo Album while Sitting Beside Her Family"
            className="singleBlogImage"
          />

          <h2>Explore a New City</h2>

          <p>
            Take your stocking fillers to a new level by gifting the experience
            of exploring a new city. There are many fun ways to explore the UK's
            best cities, from{' '}
            <BlogLink internal="/prizes/details/bring-your-baby-to-walk-voucher">
              walking tours
            </BlogLink>{' '}
            with your baby to{' '}
            <BlogLink internal="/prizes/details/york-river-sightseeing-cruise">
              river cruises
            </BlogLink>{' '}
            in the historic city of York. If you’ve got someone who would love
            to try something new, then an experience voucher is a perfect
            prezzie to make your stocking less wasteful but still make them
            merry this year. Gifting these unique experiences encourages others
            to get out of the house and try something new in 2023.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary6.jpg`}
            alt="A girl standing next to the London Eye"
            className="singleBlogImage"
          />

          <h2>Gift a creative learning experience</h2>

          <p>
            You know what they say, you’re never too old to learn! Help your
            pals and family unleash their creativity by gifting them an
            experience to get their creative juices flowing. We’ve got a ton of
            fun and unique experiences from{' '}
            <BlogLink internal="/prizes/details/neon-life-drawing-class">
              life drawing
            </BlogLink>{' '}
            in neon (hello, school disco!), to a{' '}
            <BlogLink internal="/prizes/details/london-photography-tour">
              photography tour
            </BlogLink>{' '}
            where they can take their photography skills to the next level. It’s
            the perfect way to get others to crack on with their resolutions for
            2023 with no excuses.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary7.jpg`}
            alt="A woman taking photos with her iPhone"
            className="singleBlogImage"
          />

          <h2>Sustainable Beauty Treats</h2>

          <p>
            Get stocking fillers that help others kick start their journeys to
            more sustainable and environmentally friendly living with some
            eco-friendly goodies. You can get them kitted out with bathroom
            supplies like bamboo toothbrushes, shampoo, soaps, and toiletries
            made from all-natural ingredients. By providing some plant-based
            treats, our prize partners{' '}
            <BlogLink internal="/prizes/details/naturally-legit-facial-skincare-box">
              Naturally Legit
            </BlogLink>{' '}
            and{' '}
            <BlogLink internal="/prizes/details/authentic-house-subscription">
              Authentic House
            </BlogLink>{' '}
            will give your loved ones a planet-loving start to 2023 and help
            them get on their way to feeling great and helping the planet.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary8.jpg`}
            alt="A range of Naturally Legit sustainable toiletries"
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            With the big day fast approaching, your stocking fillers will
            definitely be outside the box with a focus on sustainability and
            unique experiences. Less is definitely more, and by being more
            mindful towards our present shopping, we'll make others' days even
            more special. Make sure your stocking fillers are out to impress but
            give your loved ones a more personalised touch. We’ve got tons of
            fabulous prizes to win to treat yourself or share your winnings with
            others this holiday season. Just don’t forget your tangerines to get
            those vitamins before the feast!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary9.jpg`}
            alt="A woman shopping for Christmas stockings"
            className="singleBlogImage"
          />

          <p>
            Enter for your chance to win some festive treats! Happy holidays
            everyone from DAYMADE, and fill up your loved ones’ stockings so
            they can have their day made.
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
